import { useEffect, useMemo, useState } from 'react';
import { Announcement } from '@interfaces/general';
import { useGetAnnouncement } from './CommunicationsModal/hooks/useGetAnnouncement';
import { CommunicationsModal } from './CommunicationsModal/CommunicationsModal';
import { CommunicationsBanner } from './CommunicationsBanner/CommunicationsBanner';
import { useDismissedAnnouncements } from './shared/useDismissedAnnouncements';

/**
 * Dispatcher único del feature de Anuncios.
 *
 * Hace UNA sola llamada a /v1/announcement/active y distribuye los anuncios
 * por tipo:
 *   - modal  → cola (uno a la vez), priorizando is_persistent=true primero
 *   - banner → stack vertical fijo debajo del header (sobre cualquier página)
 *   - card   → expuesto via AnnouncementCardList para montar en pages
 */

const sortModalQueue = (items: Announcement[]): Announcement[] => {
  // Persistentes primero, luego por orden de llegada del back.
  const persistent = items.filter((a) => a.is_persistent);
  const rest = items.filter((a) => !a.is_persistent);
  return [...persistent, ...rest];
};

export const CommunicationsCenter = () => {
  const { announcements, isFetching } = useGetAnnouncement();
  const { isDismissed, dismiss } = useDismissedAnnouncements();

  const modalQueue = useMemo(
    () => sortModalQueue(announcements.filter((a) => a.announcement_type === 'modal')),
    [announcements],
  );

  const banners = useMemo(
    () => announcements.filter((a) => a.announcement_type === 'banner' && !isDismissed(a.uuid)),
    [announcements, isDismissed],
  );

  // Cola de modales — uno a la vez
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const currentModal = modalQueue[currentIndex];

  useEffect(() => {
    if (modalQueue.length > 0 && !isFetching) {
      setIsModalOpen(true);
    }
  }, [modalQueue, isFetching]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    if (currentIndex < modalQueue.length - 1) {
      setTimeout(() => {
        setCurrentIndex((prev) => prev + 1);
        setIsModalOpen(true);
      }, 500);
    }
  };

  return (
    <>
      {currentModal && (
        <CommunicationsModal announcement={currentModal} isOpen={isModalOpen} onClose={handleCloseModal} />
      )}

      {banners.length > 0 && (
        <div
          // fixed debajo del header (pt-16/lg:pt-14 del Layout) y de la
          // SubscriptionStatusBar mobile cuando aplica. z-30 queda por
          // encima del contenido pero debajo del modal (z-50 nativo).
          className="fixed left-0 right-0 z-30 flex flex-col gap-1 top-16 lg:top-14"
        >
          {banners.map((banner) => (
            <CommunicationsBanner key={banner.uuid} announcement={banner} onClose={() => dismiss(banner.uuid)} />
          ))}
        </div>
      )}

      {/* Card list — expuesto via AnnouncementCardList (commit 3) */}
    </>
  );
};
